import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ShieldCheck,
  Lock,
  FileCheck2,
  Cookie,
  Database,
  Mail,
  ArrowLeft,
  Eye,
  Server,
  KeyRound,
  Clock,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  ScrollText,
} from "lucide-react";

// Reusable trust pillar card
function Pillar({ icon: Icon, title, children }) {
  return (
    <div className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] transition">
      <div className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
        <Icon size={20} />
      </div>
      <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
      <div className="text-sm text-slate-400 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}

function Section({ id, eyebrow, title, children }) {
  return (
    <section id={id} className="py-16 px-5 border-t border-white/[0.04]">
      <div className="max-w-5xl mx-auto">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">
            {eyebrow}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="p-5 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
      <div className="font-heading text-3xl sm:text-4xl font-bold text-emerald-400">{value}</div>
      <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function FaqItem({ q, children }) {
  return (
    <details className="group p-5 rounded-2xl border border-white/[0.08] bg-white/[0.02] open:bg-white/[0.04]">
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="font-heading font-semibold text-base sm:text-lg pr-4">{q}</span>
        <span className="text-emerald-400 text-xl group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="mt-3 text-sm text-slate-400 leading-relaxed space-y-2">{children}</div>
    </details>
  );
}

export default function TrustCenterPage() {
  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden"
      data-testid="trust-center-page"
    >
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trust-back-home">
            <ArrowLeft size={16} className="text-slate-400" />
            <span className="text-sm text-slate-300">Calcio Club Manager</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white"
                data-testid="trust-login-btn"
              >
                Accedi
              </Button>
            </Link>
            <Link to="/login?mode=register">
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="trust-register-btn"
              >
                Inizia gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative py-24 px-5 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_0%,rgba(16,185,129,0.18),transparent_70%)] pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs uppercase tracking-widest mb-6">
            <ShieldCheck size={14} /> Trust Center
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
            La fiducia delle famiglie<br />
            <span className="text-emerald-400">è il nostro impegno N°1.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed">
            Dati dei minori, firme legali, pagamenti: gestiamo informazioni
            sensibili ogni giorno. Qui spieghiamo, senza giri di parole, come
            le proteggiamo — e quali diritti hai sempre garantiti.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-12">
            <Stat value="AES-256" label="Crittografia at-rest" />
            <Stat value="TLS 1.3" label="Trasporto" />
            <Stat value="GDPR" label="Conforme UE" />
            <Stat value="10 min" label="Validità OTP" />
          </div>
        </div>
      </section>

      {/* SECURITY PILLARS */}
      <Section
        id="sicurezza"
        eyebrow="Sicurezza tecnica"
        title="Come proteggiamo i dati della tua società."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Pillar icon={Lock} title="Crittografia end-to-end">
            <p>
              Tutti i dati in transito sono protetti con <strong>TLS 1.3</strong> e
              certificati gestiti automaticamente. I dati salvati sul database
              sono crittografati a riposo con <strong>AES-256</strong>.
            </p>
          </Pillar>
          <Pillar icon={KeyRound} title="Password hashate">
            <p>
              Non memorizziamo mai password in chiaro. Utilizziamo{" "}
              <strong>bcrypt</strong> con salt random per ogni utente. Anche un
              ipotetico data breach lascia le password illeggibili.
            </p>
          </Pillar>
          <Pillar icon={Server} title="Infrastruttura europea">
            <p>
              Hosting su data center con certificazione <strong>ISO 27001</strong>,{" "}
              <strong>SOC 2</strong>. Backup automatici giornalieri con
              ridondanza geografica.
            </p>
          </Pillar>
          <Pillar icon={Eye} title="Audit log immutabile">
            <p>
              Ogni firma, ogni documento generato, ogni modifica critica viene
              tracciata con timestamp, IP e user-agent. Lo storico è{" "}
              <strong>append-only</strong>: nessuno può alterare il passato.
            </p>
          </Pillar>
          <Pillar icon={ShieldCheck} title="Accesso multi-tenant">
            <p>
              Ogni società vede solo i propri dati. I genitori vedono solo i
              propri figli. Il ruolo è verificato lato server su{" "}
              <strong>ogni singola richiesta</strong>.
            </p>
          </Pillar>
          <Pillar icon={AlertTriangle} title="Protezione anti-abuso">
            <p>
              Rate-limiting su login (5 tentativi → blocco temporaneo),
              registrazione, upload pubblici e richieste OTP. Anti-spam su
              tutti gli endpoint aperti al pubblico.
            </p>
          </Pillar>
        </div>
      </Section>

      {/* OTP SIGNATURES */}
      <Section
        id="firme"
        eyebrow="Firme digitali"
        title="Come funzionano le firme OTP (validità legale)."
      >
        <div className="space-y-6">
          <p className="text-slate-300 max-w-3xl">
            Sostituiamo la firma a penna con una <strong>firma elettronica
            semplice (FES) rinforzata da OTP via email</strong>, conforme al
            <em> Regolamento eIDAS (UE) 910/2014</em>. È legalmente valida per
            consensi privacy, iscrizioni, cartellini FIGC e richieste mediche.
          </p>

          <ol className="space-y-4">
            {[
              {
                t: "Verifica del firmatario",
                d: "L'OTP può essere richiesto SOLO da un'email genitore già registrata in anagrafica per quel giocatore. Niente email arbitrarie, niente firme di terzi.",
              },
              {
                t: "Codice OTP a 6 cifre",
                d: "Inviato via email, valido 10 minuti, monouso. Dopo 5 tentativi errati il codice è invalidato.",
              },
              {
                t: "Conferma e signature record",
                d: "Una volta verificato l'OTP, registriamo: codice verificato, timestamp UTC, IP del firmatario, user-agent, hash del documento. Tutto immutabile.",
              },
              {
                t: "Distribuzione del PDF firmato",
                d: "Il documento ottiene un manifesto di firma riportante data, ora e identificativo audit. Il genitore riceve copia via email; la società conserva l'originale.",
              },
            ].map((step, i) => (
              <li
                key={i}
                className="flex gap-4 p-5 rounded-2xl border border-white/[0.08] bg-white/[0.03]"
              >
                <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-500 text-slate-950 font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <div>
                  <div className="font-heading font-semibold text-lg">{step.t}</div>
                  <p className="text-sm text-slate-400 mt-1 leading-relaxed">{step.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 flex gap-3 items-start">
            <FileCheck2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-100">
              <strong>Valore probatorio.</strong> Ai sensi del Regolamento
              eIDAS art. 25, una firma elettronica non può essere rifiutata
              come prova in giudizio solo perché non qualificata. L&apos;audit log
              completo che conserviamo è sufficiente in caso di contestazione.
            </p>
          </div>
        </div>
      </Section>

      {/* GDPR RIGHTS */}
      <Section
        id="gdpr"
        eyebrow="GDPR — I tuoi diritti"
        title="Cosa puoi fare con i dati tuoi (e di tuo figlio)."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { i: Eye, t: "Accesso", d: "Ricevi copia di tutti i dati che trattiamo." },
            { i: ScrollText, t: "Rettifica", d: "Correggi dati inesatti in qualunque momento." },
            { i: Trash2, t: "Cancellazione", d: "Diritto all'oblio: chiedi la rimozione completa." },
            { i: Database, t: "Portabilità", d: "Esporta i tuoi dati in formato JSON/CSV." },
            { i: Clock, t: "Limitazione", d: "Blocca temporaneamente il trattamento." },
            { i: AlertTriangle, t: "Opposizione", d: "Opponiti a trattamenti specifici (es. marketing)." },
          ].map((r, i) => (
            <div
              key={i}
              className="p-5 rounded-2xl border border-white/[0.08] bg-white/[0.03] flex gap-4"
            >
              <r.i size={20} className="text-emerald-400 shrink-0 mt-1" />
              <div>
                <div className="font-heading font-semibold">{r.t}</div>
                <p className="text-sm text-slate-400 mt-1">{r.d}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
          <h3 className="font-heading text-lg font-bold mb-2">Periodi di conservazione</h3>
          <ul className="text-sm text-slate-400 space-y-1.5">
            <li>
              <CheckCircle2 size={14} className="inline text-emerald-400 mr-2" />
              Anagrafiche giocatori: cancellate entro <strong>12 mesi</strong> dal termine dell&apos;attività sportiva.
            </li>
            <li>
              <CheckCircle2 size={14} className="inline text-emerald-400 mr-2" />
              Documenti firmati FIGC/privacy: <strong>10 anni</strong> (obbligo civilistico).
            </li>
            <li>
              <CheckCircle2 size={14} className="inline text-emerald-400 mr-2" />
              Fatture e ricevute pagamenti: <strong>10 anni</strong> (obbligo fiscale).
            </li>
            <li>
              <CheckCircle2 size={14} className="inline text-emerald-400 mr-2" />
              Log accessi e audit firme: <strong>5 anni</strong>.
            </li>
            <li>
              <CheckCircle2 size={14} className="inline text-emerald-400 mr-2" />
              Backup automatici: rotazione <strong>30 giorni</strong>.
            </li>
          </ul>
        </div>
      </Section>

      {/* COOKIES */}
      <Section
        id="cookie"
        eyebrow="Cookie & Tracciamento"
        title="Trasparenza totale, sotto il tuo controllo."
      >
        <div className="space-y-4 max-w-3xl">
          <p className="text-slate-300">
            Usiamo il banner di consenso <strong>Cookiebot</strong> (certificato
            ISO 27001). Nessun cookie di marketing o analytics viene attivato
            prima del tuo esplicito consenso.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="p-5 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <div className="flex items-center gap-2 mb-2">
                <Cookie size={18} className="text-emerald-400" />
                <span className="font-heading font-semibold">Cookie necessari</span>
              </div>
              <p className="text-sm text-slate-400">
                Mantengono la sessione attiva e proteggono da CSRF. Sempre attivi.
                Esempi: <code className="text-xs bg-white/10 px-1 py-0.5 rounded">access_token</code>,{" "}
                <code className="text-xs bg-white/10 px-1 py-0.5 rounded">refresh_token</code>.
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <div className="flex items-center gap-2 mb-2">
                <Cookie size={18} className="text-amber-400" />
                <span className="font-heading font-semibold">Marketing & Analytics</span>
              </div>
              <p className="text-sm text-slate-400">
                Google Ads/Analytics per misurare l&apos;efficacia delle campagne. Attivi
                solo dopo consenso esplicito.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <Button
              onClick={() => {
                // Cookiebot exposes Cookiebot.show() to reopen the banner
                if (typeof window !== "undefined" && window.Cookiebot) {
                  window.Cookiebot.show();
                } else {
                  window.alert("Banner consensi attualmente non disponibile.");
                }
              }}
              className="bg-white/10 hover:bg-white/15 text-white"
              data-testid="trust-cookie-settings-btn"
            >
              Modifica preferenze cookie
            </Button>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" eyebrow="FAQ" title="Domande che ci fanno spesso le famiglie.">
        <div className="space-y-3 max-w-3xl">
          <FaqItem q="Dove finiscono i dati di mio figlio?">
            <p>
              Tutti i dati sono ospitati su server con sede nell&apos;Unione
              Europea. Non vendiamo né condividiamo dati con terzi a fini
              commerciali. Eventuali fornitori (es. Stripe per i pagamenti)
              sono Responsabili del trattamento e firmano DPA conformi GDPR.
            </p>
          </FaqItem>
          <FaqItem q="Posso cancellare il mio account quando voglio?">
            <p>
              Sì. Scrivi a{" "}
              <a
                href="mailto:privacy@calcioclubmanager.com"
                className="text-emerald-400 underline"
              >
                privacy@calcioclubmanager.com
              </a>{" "}
              e cancelliamo dati e backup entro 30 giorni, ad eccezione di
              documenti che dobbiamo conservare per obblighi di legge (firme
              FIGC, fatture) i quali restano cifrati e accessibili solo per
              eventuali esibizioni alle autorità.
            </p>
          </FaqItem>
          <FaqItem q="La firma OTP via email vale davvero in giudizio?">
            <p>
              Sì. Il Regolamento eIDAS art. 25 stabilisce che a una firma
              elettronica non può essere negato valore probatorio solo perché
              elettronica. L&apos;audit log completo che conserviamo (codice OTP
              verificato, timestamp UTC, IP, hash del documento) costituisce
              prova sufficiente nella stragrande maggioranza dei contesti
              sportivi e amministrativi.
            </p>
          </FaqItem>
          <FaqItem q="Cosa succede se la società smette di pagare l&apos;abbonamento?">
            <p>
              I dati rimangono accessibili in modalità &quot;sola lettura&quot; per 60
              giorni. Durante questo periodo puoi esportare tutto (rosa, firme,
              documenti, pagamenti) in PDF/CSV. Dopo 60 giorni i dati vengono
              archiviati cifrati per ulteriori 30 giorni, poi cancellati
              definitivamente.
            </p>
          </FaqItem>
          <FaqItem q="Avete subito attacchi o data breach?">
            <p>
              Nessun data breach noto al{" "}
              <strong>{new Date().toLocaleDateString("it-IT", { year: "numeric", month: "long" })}</strong>.
              In caso ne dovesse verificarsi uno, ti notificheremo entro 72 ore
              come richiesto dall&apos;art. 33 GDPR, indicando esattamente quali
              dati sono stati esposti e cosa fare.
            </p>
          </FaqItem>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contatti" eyebrow="Contatti privacy" title="Hai una domanda? Scrivici.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <a
            href="mailto:privacy@calcioclubmanager.com"
            className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] transition flex items-start gap-4"
            data-testid="trust-contact-privacy"
          >
            <Mail size={22} className="text-emerald-400 shrink-0 mt-1" />
            <div>
              <div className="font-heading font-semibold">Data Protection Officer</div>
              <div className="text-sm text-emerald-400 mt-1">privacy@calcioclubmanager.com</div>
              <p className="text-xs text-slate-400 mt-2">
                Per esercitare i tuoi diritti GDPR, richieste di cancellazione o
                segnalazioni privacy.
              </p>
            </div>
          </a>
          <a
            href="mailto:security@calcioclubmanager.com"
            className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] transition flex items-start gap-4"
            data-testid="trust-contact-security"
          >
            <ShieldCheck size={22} className="text-emerald-400 shrink-0 mt-1" />
            <div>
              <div className="font-heading font-semibold">Security disclosure</div>
              <div className="text-sm text-emerald-400 mt-1">security@calcioclubmanager.com</div>
              <p className="text-xs text-slate-400 mt-2">
                Hai trovato una vulnerabilità? Comunicacela responsabilmente:
                rispondiamo entro 48 ore.
              </p>
            </div>
          </a>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="px-5 py-12 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-lg">Calcio Club Manager</div>
            <p className="text-xs text-slate-500 mt-1">
              © {new Date().getFullYear()} · Tutti i diritti riservati.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400">
            <Link to="/" className="hover:text-emerald-400">Home</Link>
            <Link to="/login" className="hover:text-emerald-400">Accedi</Link>
            <a href="#sicurezza" className="hover:text-emerald-400">Sicurezza</a>
            <a href="#firme" className="hover:text-emerald-400">Firme</a>
            <a href="#gdpr" className="hover:text-emerald-400">GDPR</a>
            <a href="#cookie" className="hover:text-emerald-400">Cookie</a>
            <a href="#contatti" className="hover:text-emerald-400">Contatti</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
